import React from 'react';
import { ColorValue, StyleProp, View, ViewStyle } from 'react-native';
import { faGift } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import type { MissionPromotionDisplayDto, MissionPromotionDto } from '../../../api/api.g';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import { promotionDateLabel } from '../../../helpers/missionPromotions';
import Label, { LabelType } from '../Label';
import CustomMarkdown from '../CustomMarkDown';

interface PromotionProps {
  display?: MissionPromotionDisplayDto;
}

/** Compact list badge; presses follow the surrounding mission row. */
export default function MissionPromotion({ display }: PromotionProps) {
  const theme = useCustomTheme();
  const t = useTranslation();
  const language = useAppSelector(state => state.userPrefs.uiLanguage) ?? 'en';
  const promotions = display?.promotions ?? [];
  if (!promotions.length) return null;
  const color = theme.isDark ? '#F7D58B' : '#704500';
  const backgroundColor = theme.isDark ? '#453719' : '#FFF3CF';
  return (
    <View style={{ marginTop: 6, minWidth: 0, maxWidth: '100%' }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', alignSelf: 'flex-start', maxWidth: '100%', gap: 6,
        paddingVertical: 5, paddingHorizontal: 8, borderRadius: 6, backgroundColor }}>
        <FontAwesomeIcon icon={faGift} size={16} color={color} style={{ marginTop: 1 }} />
        <Label variant={LabelType.caption} style={{ color, fontWeight: '700', flexShrink: 1 }}>
          {promotions.length === 1 ? promotions[0].label : t('mission-promotions-available', {
            defaultValue: language.startsWith('fr') ? 'Promotions disponibles' : 'Promotions available',
          })}
        </Label>
      </View>
    </View>
  );
}

/** Short offers belong in the compensation box; long content belongs in the information panel. */
export function MissionPromotionSummary({ display, color, iconColor, separated = true }: PromotionProps & { color?: ColorValue; iconColor?: ColorValue; separated?: boolean }) {
  const theme = useCustomTheme();
  const promotions = display?.promotions ?? [];
  if (!promotions.length) return null;
  return (
    <View testID="mission-promotion-summary" style={{ width: '100%', minWidth: 0, gap: 8, padding: 8,
      ...(separated ? { borderTopWidth: 1, borderTopColor: theme.borderColorLight, paddingTop: 10, marginTop: 6 } : {}) }}>
      {promotions.map(promotion => (
        <View key={promotion.periodMissionConfigurationId} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
          <FontAwesomeIcon icon={faGift} size={18} color={(iconColor ?? theme.text.primary).toString()} style={{ marginTop: 1 }} />
          <Label variant={LabelType.titleDarkSmall} style={{ color: color ?? theme.text.titleDark, fontWeight: '700', flexShrink: 1 }}>
            {promotion.label}
          </Label>
        </View>
      ))}
    </View>
  );
}

interface SectionAppearance {
  infoBlocksBase: StyleProp<ViewStyle>;
  infoBlocks: {
    title: StyleProp<ViewStyle>;
    titleIcon: React.ComponentProps<typeof FontAwesomeIcon>['style'];
    content: StyleProp<ViewStyle>;
  };
}

const hasDetails = (promotion: MissionPromotionDto) => !!(promotion.details || promotion.startDate || promotion.endDate);

/** Uses the host panel's existing section styles, including mission-type color overrides. */
export function MissionPromotionSection({ display, appearance, titleColor, bodyColor }: PromotionProps & {
  appearance: SectionAppearance;
  titleColor?: ColorValue;
  bodyColor?: ColorValue;
}) {
  const t = useTranslation();
  const promotions = display?.promotions ?? [];
  if (!promotions.some(hasDetails)) return null;
  return (
    <View testID="mission-promotion-section" style={appearance.infoBlocksBase}>
      <View style={appearance.infoBlocks.title}>
        <FontAwesomeIcon icon={faGift} style={appearance.infoBlocks.titleIcon} color={titleColor?.toString()} />
        <Label accessibilityRole="header" variant={LabelType.titleDark} style={{ flexShrink: 1, ...(titleColor ? { color: titleColor } : {}) }}>
          {t('mission-promotion-title', { defaultValue: 'Promotion' })}
        </Label>
      </View>
      <View style={appearance.infoBlocks.content}>
        {promotions.map((promotion, index) => (
          <View key={promotion.periodMissionConfigurationId} style={index ? { marginTop: 12 } : undefined}>
            {promotions.length > 1 && <Label style={{ color: bodyColor, fontWeight: '600', marginBottom: 4 }}>{promotion.label}</Label>}
            {!!promotion.details && <CustomMarkdown style={bodyColor ? { body: { color: bodyColor, lineHeight: 16 } } : undefined}>{promotion.details}</CustomMarkdown>}
            <PromotionDates promotion={promotion} color={bodyColor} />
          </View>
        ))}
      </View>
    </View>
  );
}

function PromotionDates({ promotion, color }: { promotion: MissionPromotionDto; color?: ColorValue }) {
  const t = useTranslation();
  const language = useAppSelector(state => state.userPrefs.uiLanguage) ?? 'en';
  if (!promotion.startDate && !promotion.endDate) return null;
  return (
    <Label variant={LabelType.caption} style={{ marginTop: 5, ...(color ? { color } : {}) }}>
      {promotion.startDate && t('mission-promotion-from', {
        date: promotionDateLabel(promotion.startDate, language), defaultValue: language.startsWith('fr') ? 'Dès le %{date}' : 'From %{date}',
      })}
      {promotion.startDate && promotion.endDate ? ' · ' : ''}
      {promotion.endDate && t('mission-promotion-through', {
        date: promotionDateLabel(promotion.endDate, language), defaultValue: language.startsWith('fr') ? 'Jusqu’au %{date} inclusivement' : 'Through %{date} inclusive',
      })}
      {` (${promotion.timeZoneId})`}
    </Label>
  );
}
